<!-- src/components/sections/Hero.svelte -->
<script>
  import Button from '../common/Button.svelte';
  import Image from '../common/Image.svelte';
  import hero_content from '../../content/hero.json';
  import logo from '../../assets/down-to-earth-logo-arrow-sign.svg';
  import { BOOKING_URL } from '../../lib/config.js';

  const hero_quote_sizes = `
    (max-width: 480px) 280px,
    (max-width: 768px) 350px,
    (max-width: 1024px) 400px,
    450px
  `.trim();
</script>

<section class="hero">
  <!-- Primary headline early in the page structure for better SEO detection -->
  <h1 class="visually-hidden">Down to Earth Guides: Real, Measurable AI for Your Business — in Plain English</h1>

  <div class="hero_container">
    <div class="hero_content">
      <div class="hero_text animate-fade-in">
        <div class="logo_section">
          <img
            src={logo}
            alt="Down to Earth Guides Logo"
            class="hero_logo"
            width="60"
            height="60"
            loading="eager"
          />
          <p class="hero_title text-primary">{hero_content.kicker}</p>
        </div>

        <h2 class="hero_headline marker-font" aria-level="1">{hero_content.headline}</h2>
        <h3 class="hero_subheadline">{hero_content.subheadline}</h3>
        <p class="hero_description">{hero_content.description}</p>

        <div class="cta_row">
          <Button href={BOOKING_URL} variant="primary" size="large">
            {hero_content.primary_cta.text}
          </Button>
          <Button href={hero_content.secondary_cta.link} variant="outline" size="large">
            {hero_content.secondary_cta.text}
          </Button>
        </div>
        <p class="cta_subtext">
          The AI LIFT is a fixed-scope, fixed-price assessment — or start free with the plain-English series.
        </p>
      </div>

      <div class="hero_quote animate-slide-up">
        <div class="quote_container">
          <div class="quote_image_container">
            <Image
              src={hero_content.lever.image}
              alt={hero_content.lever.alt}
              loading="eager"
              width="450"
              height="450"
              class_name="hero_quote_image"
              sizes_override={hero_quote_sizes}
            />
          </div>
          <div class="quote_attribution">
            <p class="quote_text">"{hero_content.lever.quote}"</p>
            <p class="quote_author">{hero_content.lever.attribution}</p>
            <p class="quote_source">{hero_content.lever.note}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  .hero {
    background: var(--color-background);
    padding: 6rem 0 4rem;
    position: relative;
    overflow: hidden;
  }

  .hero_container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
  }

  .hero_content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
  }

  .logo_section {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }

  .hero_logo {
    width: 60px;
    height: 60px;
  }

  .hero_title {
    font-size: 1.5rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin: 0;
  }

  /* Visually hidden but still accessible to screen readers and crawlers */
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .hero_headline {
    font-size: 3.5rem;
    line-height: 1.1;
    margin-bottom: 1.5rem;
    color: var(--color-text);
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  }

  .hero_subheadline {
    font-size: 1.75rem;
    line-height: 1.4;
    margin-bottom: 1.5rem;
    color: var(--color-text-muted);
    font-weight: 500;
  }

  .hero_description {
    font-size: 1.2rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    color: var(--color-text-muted);
  }

  .cta_row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .cta_subtext {
    font-size: 0.95rem;
    color: var(--color-text-muted);
    margin: 0;
  }

  .hero_quote {
    position: relative;
  }

  .quote_container {
    position: relative;
    padding: 2rem;
    background: var(--color-card-bg);
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }

  .quote_image_container {
    width: 100%;
    max-width: 450px;
    position: relative;
    margin: 0 auto 1.5rem;
  }

  .quote_image_container::before {
    content: "";
    display: block;
    padding-top: 100%; /* 1:1 Aspect Ratio */
  }

  :global(.hero_quote_image) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 12px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }

  .quote_attribution {
    text-align: left;
    padding: 0 1rem;
  }

  .quote_text {
    font-size: 1.25rem;
    line-height: 1.6;
    color: var(--color-text);
    margin-bottom: 1rem;
    font-style: italic;
  }

  .quote_author {
    font-size: 1.1rem;
    color: var(--color-text-muted);
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  .quote_source {
    font-size: 0.95rem;
    color: var(--color-text-muted);
    opacity: 0.85;
  }

  @media (max-width: 1024px) {
    .hero_content {
      grid-template-columns: 1fr;
      gap: 2rem;
      text-align: center;
    }

    .logo_section {
      justify-content: center;
    }

    .cta_row {
      justify-content: center;
    }

    .hero_quote {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .hero {
      padding: 4rem 0 2rem;
    }

    .hero_headline {
      font-size: 2.5rem;
    }

    .hero_subheadline {
      font-size: 1.5rem;
    }

    .hero_description {
      font-size: 1.125rem;
    }
  }
</style>
